/**
 * PreachKit first-run onboarding tour.
 *
 * Shown ONCE per user (localStorage-gated) when they first open /preach-kit.
 * Walks through the 7 tabs with a small illustration and a "Skip" / "Next" /
 * "Got it!" flow. Can be re-opened by visiting `?tour=1`.
 */
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Sparkles, BookOpen, Search, MessageSquare, FileText, Scissors,
  BookMarked, X, ChevronRight, ChevronLeft, ScrollText,
} from "lucide-react";

const STORAGE_KEY = "preachkit_onboarding_v1";

// Step metadata is static; titles/bodies are translated at render-time.
const STEP_META = [
  { id: "welcome",   icon: ScrollText,    color: "from-amber-500/30 to-amber-700/10" },
  { id: "outline",   icon: Sparkles,      color: "from-amber-500/30 to-orange-700/10" },
  { id: "scripture", icon: BookOpen,      color: "from-orange-500/30 to-red-700/10" },
  { id: "search",    icon: Search,        color: "from-emerald-500/30 to-teal-700/10" },
  { id: "companion", icon: MessageSquare, color: "from-cyan-500/30 to-blue-700/10" },
  { id: "notes",     icon: FileText,      color: "from-purple-500/30 to-fuchsia-700/10" },
  { id: "clipper",   icon: Scissors,      color: "from-pink-500/30 to-rose-700/10" },
  { id: "library",   icon: BookMarked,    color: "from-amber-500/30 to-yellow-700/10" },
];

// English fallbacks live alongside translation keys so the tour still renders
// gracefully if a locale is missing keys.
const FALLBACKS = {
  welcome:   { title: "Welcome to PreachKit",          body: "Your AI co-pastor for sermon prep, video clips, and Bible study. Let me show you the 7 tools in your toolkit — takes 30 seconds." },
  outline:   { title: "Sermon Outline",                body: "Type a topic or scripture; AI returns a 3-point outline with introduction, illustrations, application, and a punchy CTA. Edit any field inline before saving." },
  scripture: { title: "Scripture → Slides",            body: "Paste a passage; AI builds a ready-to-present slide deck. One click sends it straight to the SlideCon editor." },
  search:    { title: "Bible Search",                  body: "Find verses by topic, with AI cross-references. Click any reference to expand the full verse text inline." },
  companion: { title: "Sermon Companion",              body: "Multi-turn chat with Claude for Greek/Hebrew word studies, historical context, and theological questions. Long conversations auto-summarize so context never runs out." },
  notes:     { title: "Notes → Sermon",                body: "Got raw notes? Paste them and AI organizes them into a polished outline — preserving your best quotes." },
  clipper:   { title: "Sermon Video Clipper",          body: "Upload your Sunday sermon video; AI picks 5 shareable moments, renders them in 9:16 + 1:1, optionally with burnt-in captions. One-click share to Reels, TikTok, Shorts." },
  library:   { title: "Library + Send to Editor",      body: "Every saved sermon lives in your Library. Click 'Send to Editor' on any outline to turn it into a real presentation in seconds." },
};

export default function PreachKitOnboarding({ forceOpen = false }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  const STEPS = useMemo(
    () => STEP_META.map((m) => ({
      icon: m.icon,
      color: m.color,
      title: t(`onboarding_${m.id}_title`) || FALLBACKS[m.id].title,
      body:  t(`onboarding_${m.id}_body`)  || FALLBACKS[m.id].body,
    })),
    [t]
  );

  useEffect(() => {
    if (forceOpen) {
      setStep(0);
      setOpen(true);
      return;
    }
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("tour") === "1") {
        setStep(0);
        setOpen(true);
        return;
      }
      const seen = localStorage.getItem(STORAGE_KEY);
      if (!seen) setOpen(true);
    } catch { /* SSR */ }
  }, [forceOpen]);

  const close = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    setOpen(false);
    setStep(0);
  };

  const next = () => (step < STEPS.length - 1 ? setStep(step + 1) : close());
  const prev = () => step > 0 && setStep(step - 1);

  const current = STEPS[step];
  const Icon = current?.icon || ScrollText;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="sm:max-w-md" data-testid="onboarding-dialog">
        <DialogHeader>
          <div className={`relative -mx-6 -mt-6 mb-3 h-32 bg-gradient-to-br ${current.color} flex items-center justify-center rounded-t-lg`}>
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ scale: 0.7, opacity: 0, rotate: -10 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                exit={{ scale: 0.7, opacity: 0, rotate: 10 }}
                transition={{ type: "spring", duration: 0.4 }}
              >
                <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center">
                  <Icon className="w-8 h-8 text-amber-100" />
                </div>
              </motion.div>
            </AnimatePresence>
            <button
              onClick={close}
              className="absolute top-2 right-2 text-white/70 hover:text-white"
              aria-label={t("onboarding_skip_aria") || "Skip tour"}
              data-testid="onboarding-skip"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="absolute bottom-2 right-3 text-[10px] text-white/60">
              {step + 1} / {STEPS.length}
            </div>
          </div>
          <DialogTitle data-testid="onboarding-step-title">{current.title}</DialogTitle>
          <DialogDescription>{current.body}</DialogDescription>
        </DialogHeader>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 my-2">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-amber-500" : "w-1.5 bg-slate-700"}`}
            />
          ))}
        </div>

        <DialogFooter className="flex items-center justify-between gap-2">
          {step > 0 ? (
            <Button variant="ghost" size="sm" onClick={prev} data-testid="onboarding-back">
              <ChevronLeft className="w-3.5 h-3.5 mr-1" /> {t("back") || "Back"}
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={close} data-testid="onboarding-skip-text">
              {t("onboarding_skip_tour") || "Skip tour"}
            </Button>
          )}
          <Button onClick={next} className="bg-amber-600 hover:bg-amber-700" data-testid="onboarding-next">
            {step === STEPS.length - 1 ? (t("onboarding_got_it") || "Got it!") : (t("next") || "Next")}
            {step < STEPS.length - 1 && <ChevronRight className="w-3.5 h-3.5 ml-1" />}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
